import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

const stepLabels = ["Connection", "Brand", "Design", "Products", "Store", "Launch"];

const defaultStoreMode = "quote";

export default function PromoShowcaseLaunchWizard({
  open,
  onClose,
  settings,
  templates,
  selectedTemplateId,
  templateForm,
  storeProductSummary,
  launching,
  launchVerification,
  connecting,
  provisioning,
  onProvision,
  onReconnect,
  onOpenSettings,
  onOpenProducts,
  onOpenCatalogues,
  onUploadLogo,
  onLaunch,
}) {
  const [step, setStep] = useState(0);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    templateId: "",
    siteName: "",
    siteSlug: "",
    brandName: "",
    logoUrl: "",
    primaryColor: "#B91C1C",
    secondaryColor: "#111111",
    announcementText: "",
    tagline: "",
    metaDescription: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: "",
    storeMode: defaultStoreMode,
    syncProducts: true,
  });

  const starterTemplates = useMemo(
    () => (templates || []).filter((template) => String(template?.visibility || "public").toLowerCase() !== "private"),
    [templates]
  );
  const savedTemplates = useMemo(
    () => (templates || []).filter((template) => String(template?.visibility || "").toLowerCase() === "private"),
    [templates]
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm({
      templateId:
        starterTemplates.some((template) => String(template?.id) === String(selectedTemplateId || ""))
          ? selectedTemplateId
          : starterTemplates?.[0]?.id || selectedTemplateId || "",
      siteName: templateForm?.brandName || "",
      siteSlug: "",
      brandName: templateForm?.brandName || "",
      logoUrl: templateForm?.logoUrl || "",
      primaryColor: templateForm?.primaryColor || "#B91C1C",
      secondaryColor: templateForm?.secondaryColor || "#111111",
      announcementText: templateForm?.announcementText || "",
      tagline: templateForm?.siteIdentity?.tagline || "",
      metaDescription: templateForm?.siteIdentity?.meta_description || "",
      contactEmail: templateForm?.siteIdentity?.contact?.email || "",
      contactPhone: templateForm?.siteIdentity?.contact?.phone || "",
      contactAddress: templateForm?.siteIdentity?.contact?.address || "",
      storeMode:
        templateForm?.storeSettings?.payment_methods?.stripe
          ? "checkout"
          : templateForm?.storeSettings?.payment_methods?.request_quote
          ? "quote"
          : defaultStoreMode,
      syncProducts: true,
    });
  }, [open, selectedTemplateId, starterTemplates, templateForm]);

  const connectionReady = !!settings?.promoShowcaseConnected;
  const productCount = Number(storeProductSummary?.importedCount || 0);
  const productReady = productCount > 0;
  const selectedTemplate = useMemo(
    () => (templates || []).find((item) => String(item?.id) === String(form.templateId)) || null,
    [form.templateId, templates]
  );

  const canContinue = [
    connectionReady,
    !!String(form.brandName || "").trim(),
    !!selectedTemplate,
    true,
    true,
    connectionReady && !!String(form.brandName || "").trim() && !!selectedTemplate,
  ][step];

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const statusRow = (label, done, detail) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.8 }}>
      <Box>
        <Typography sx={{ fontWeight: 800 }}>{label}</Typography>
        {detail ? <Typography variant="caption" color="text.secondary">{detail}</Typography> : null}
      </Box>
      <Chip
        size="small"
        icon={done ? <CheckCircleRoundedIcon /> : undefined}
        label={done ? "Ready" : "Needs setup"}
        color={done ? "success" : "default"}
        variant={done ? "filled" : "outlined"}
      />
    </Box>
  );

  return (
    <Dialog open={open} onClose={launching ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
        <RocketLaunchRoundedIcon />
        Launch Showcase
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2.2 }}>
          <Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
              {stepLabels.map((label, index) => (
                <Chip
                  key={label}
                  size="small"
                  label={label}
                  color={index === step ? "primary" : index < step ? "success" : "default"}
                  variant={index <= step ? "filled" : "outlined"}
                  onClick={() => !launching && setStep(index)}
                />
              ))}
            </Box>
            <LinearProgress variant="determinate" value={((step + 1) / stepLabels.length) * 100} />
          </Box>

          {step === 0 && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography variant="h6">{connectionReady ? "Storefront connection" : "Create your Showcase website"}</Typography>
              {!connectionReady ? (
                <Box sx={{ display: "grid", gap: 1.25, p: 1.5, border: "1px solid rgba(15,23,42,.12)", borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Elite Core will create a new site inside showcase.elitecore.ca, connect it securely, and prepare it for the launch wizard.
                  </Typography>
                  <TextField
                    label="Website name"
                    value={form.siteName}
                    onChange={(event) => {
                      const value = event.target.value;
                      setField("siteName", value);
                      if (!String(form.brandName || "").trim()) setField("brandName", value);
                    }}
                    placeholder="Acme Employee Store"
                    required
                    fullWidth
                  />
                  <TextField
                    label="Website address"
                    value={form.siteSlug}
                    onChange={(event) => setField("siteSlug", event.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, ""))}
                    placeholder="acme"
                    helperText={`https://showcase.elitecore.ca/${form.siteSlug || "your-company"}/ — if the address is taken, Elite Core will allocate the next available suffix.`}
                    fullWidth
                  />
                  {provisioning ? (
                    <Box sx={{ display: "grid", gap: .75 }}>
                      <LinearProgress />
                      <Typography variant="caption" color="text.secondary">
                        Creating WordPress site, assigning its site ID, connecting Elite Core, and checking runtime capabilities…
                      </Typography>
                    </Box>
                  ) : null}
                  <Button
                    variant="contained"
                    disabled={provisioning || !String(form.siteName || "").trim()}
                    onClick={() => onProvision?.({ title: form.siteName, slug: form.siteSlug })}
                  >
                    {provisioning ? "Creating website..." : "Create website"}
                  </Button>
                </Box>
              ) : null}
              {statusRow(
                "WordPress runtime",
                connectionReady,
                settings?.promoShowcaseUrl || "No WordPress URL configured"
              )}
              {statusRow(
                "Multisite hosting",
                settings?.promoShowcaseHosting?.detectedMultisite === true,
                settings?.promoShowcaseHosting?.runtimeSiteUrl
                  || settings?.promoShowcaseHosting?.networkBaseUrl
                  || "Expected network: https://showcase.elitecore.ca"
              )}
              {settings?.promoShowcaseHosting ? (
                <Box sx={{ display: "grid", gap: .35, p: 1.25, border: "1px solid rgba(15,23,42,.10)", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Network: {settings.promoShowcaseHosting.runtimeNetworkUrl || settings.promoShowcaseHosting.networkBaseUrl || "https://showcase.elitecore.ca"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Site path: {settings.promoShowcaseHosting.runtimeSitePath || settings.promoShowcaseHosting.sitePath || "/"}
                  </Typography>
                  {settings.promoShowcaseHosting.blogId ? (
                    <Typography variant="caption" color="text.secondary">
                      WordPress site ID: {settings.promoShowcaseHosting.blogId}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    Provisioning: {settings.promoShowcaseHosting.provisioningStatus || (settings.promoShowcaseHosting.provisioningManagedByCore ? "managed by Elite Core" : "manual")}
                  </Typography>
                </Box>
              ) : null}
              {statusRow(
                "WooCommerce",
                settings?.templateCapabilities?.environment?.woocommerce_active === true,
                settings?.templateCapabilities?.environment?.woocommerce_version
                  ? `Version ${settings.templateCapabilities.environment.woocommerce_version}`
                  : "Runtime has not reported WooCommerce readiness yet"
              )}
              {settings?.templateCapabilities?.contract ? (
                <Typography variant="body2" color="text.secondary">
                  Contract: {settings.templateCapabilities.contract} v{settings.templateCapabilities.contract_version || "?"}
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={onReconnect} disabled={connecting}>
                  {connecting ? "Connecting..." : connectionReady ? "Reconnect" : "Connect now"}
                </Button>
                <Button variant="outlined" onClick={onOpenSettings}>Connection settings</Button>
                {(settings?.promoShowcaseHosting?.publicUrl || settings?.promoShowcaseHosting?.effectivePublicUrl || settings?.promoShowcaseUrl) ? (
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNewRoundedIcon />}
                    onClick={() => window.open(
                      settings?.promoShowcaseHosting?.publicUrl
                      || settings?.promoShowcaseHosting?.effectivePublicUrl
                      || settings.promoShowcaseUrl,
                      "_blank"
                    )}
                  >
                    Open public site
                  </Button>
                ) : null}
              </Box>
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography variant="h6">Brand it</Typography>
              <TextField
                label="Company / Store name"
                value={form.brandName}
                onChange={(event) => setField("brandName", event.target.value)}
                required
                fullWidth
              />
              <Box sx={{ display: "grid", gap: 1 }}>
                <TextField
                  label="Logo URL"
                  value={form.logoUrl}
                  onChange={(event) => setField("logoUrl", event.target.value)}
                  helperText="Paste an existing URL or upload a PNG, JPG, or WebP directly to this Showcase."
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Button component="label" variant="outlined" disabled={uploadingLogo || !connectionReady}>
                    {uploadingLogo ? "Uploading logo..." : "Upload logo"}
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (!file || !onUploadLogo) return;
                        setUploadingLogo(true);
                        try {
                          const url = await onUploadLogo(file);
                          if (url) setField("logoUrl", url);
                        } finally {
                          setUploadingLogo(false);
                        }
                      }}
                    />
                  </Button>
                  {form.logoUrl ? (
                    <Box
                      component="img"
                      src={form.logoUrl}
                      alt="Store logo preview"
                      sx={{ maxWidth: 160, maxHeight: 56, objectFit: "contain", border: "1px solid rgba(15,23,42,.1)", p: 0.5 }}
                    />
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <TextField
                  label="Primary colour"
                  value={form.primaryColor}
                  onChange={(event) => setField("primaryColor", event.target.value)}
                />
                <TextField
                  label="Secondary colour"
                  value={form.secondaryColor}
                  onChange={(event) => setField("secondaryColor", event.target.value)}
                />
              </Box>
              <TextField
                label="Announcement (optional)"
                value={form.announcementText}
                onChange={(event) => setField("announcementText", event.target.value)}
                fullWidth
              />
              <Divider />
              <Typography sx={{ fontWeight: 800 }}>Company details</Typography>
              <TextField
                label="Tagline"
                value={form.tagline}
                onChange={(event) => setField("tagline", event.target.value)}
                helperText="Used as the WordPress tagline and as a fallback SEO description."
                fullWidth
              />
              <TextField
                label="SEO description"
                value={form.metaDescription}
                onChange={(event) => setField("metaDescription", event.target.value)}
                helperText="Short search/social description for the launched site."
                fullWidth
                multiline
                minRows={2}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <TextField
                  label="Public email"
                  type="email"
                  value={form.contactEmail}
                  onChange={(event) => setField("contactEmail", event.target.value)}
                />
                <TextField
                  label="Public phone"
                  value={form.contactPhone}
                  onChange={(event) => setField("contactPhone", event.target.value)}
                />
              </Box>
              <TextField
                label="Public address"
                value={form.contactAddress}
                onChange={(event) => setField("contactAddress", event.target.value)}
                fullWidth
              />
            </Box>
          )}

          {step === 2 && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box>
                <Typography variant="h6">Choose a complete site design</Typography>
                <Typography variant="body2" color="text.secondary">
                  Each starter publishes a coordinated Home, Catalog, Services, About and Contact site in one go.
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                {starterTemplates.map((template) => {
                  const active = String(template?.id) === String(form.templateId);
                  const theme = template?.defaults?.theme_vars || {};
                  const homeWidgets = template?.defaults?.layout_schema?.slots?.home || [];
                  const hero = homeWidgets.find((widget) => widget?.component === "hero") || {};
                  const heroProps = hero?.props || {};
                  const primary = theme.primary_color || "#111827";
                  const secondary = theme.secondary_color || "#B91C1C";
                  const heroTitle = heroProps.title || "Your Brand";
                  const heroSubtitle = heroProps.subtitle || template?.description || "";
                  const pageCount = Array.isArray(template?.defaults?.pages) ? template.defaults.pages.length : 5;

                  return (
                    <Box
                      key={template.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setField("templateId", template.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") setField("templateId", template.id);
                      }}
                      sx={{
                        borderRadius: 2.5,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: active ? "2px solid #B91C1C" : "1px solid rgba(15,23,42,.12)",
                        boxShadow: active ? "0 10px 28px rgba(185,28,28,.14)" : "0 6px 18px rgba(15,23,42,.06)",
                        background: "#fff",
                        transition: "transform 160ms ease, box-shadow 160ms ease",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 28px rgba(15,23,42,.11)" },
                      }}
                    >
                      <Box sx={{ p: 1, background: "#f8fafc" }}>
                        <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: "1px solid rgba(15,23,42,.1)", background: "#fff", aspectRatio: "16 / 10" }}>
                          <Box sx={{ px: 1, py: .75, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(15,23,42,.08)" }}>
                            <Box sx={{ width: 38, height: 6, borderRadius: 4, bgcolor: primary }} />
                            <Box sx={{ display: "flex", gap: .5 }}>
                              {["Home", "Catalog", "Services", "About"].map((item) => (
                                <Typography key={item} sx={{ fontSize: 5.2, color: "#64748b", lineHeight: 1 }}>{item}</Typography>
                              ))}
                            </Box>
                          </Box>
                          <Box sx={{ minHeight: 104, px: 1.75, py: 2, display: "flex", flexDirection: "column", justifyContent: "center", background: `linear-gradient(135deg, ${primary}, ${secondary})`, color: "#fff" }}>
                            <Typography sx={{ fontSize: 14, lineHeight: 1.05, fontWeight: 900, maxWidth: "82%" }}>{heroTitle}</Typography>
                            <Typography sx={{ mt: .65, fontSize: 7, lineHeight: 1.3, opacity: .88, maxWidth: "86%" }}>{heroSubtitle}</Typography>
                            <Box sx={{ mt: 1.1, width: 43, height: 13, borderRadius: 8, bgcolor: "#fff", opacity: .96 }} />
                          </Box>
                          <Box sx={{ p: .8, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: .5 }}>
                            {[0, 1, 2, 3].map((item) => (
                              <Box key={item} sx={{ height: 20, borderRadius: .7, bgcolor: "#eef2f7", border: "1px solid #e2e8f0" }} />
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ p: 1.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{template.name || template.id}</Typography>
                          {active ? <Chip size="small" label="Selected" color="error" /> : null}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .4, minHeight: 34 }}>
                          {template.description || "Ready-to-publish Showcase design."}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary" }}>
                          {pageCount} pages included
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {savedTemplates.length ? (
                <Box sx={{ p: 1.25, border: "1px solid rgba(15,23,42,.1)", borderRadius: 2, bgcolor: "rgba(248,250,252,.85)" }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Or launch one of your saved drafts</Typography>
                  <TextField
                    select
                    size="small"
                    label={`Saved drafts (${savedTemplates.length})`}
                    value={savedTemplates.some((template) => String(template.id) === String(form.templateId)) ? form.templateId : ""}
                    onChange={(event) => setField("templateId", event.target.value)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {savedTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {String(template.name || "Saved Showcase draft").replace(/PromoShowcase/gi, "Showcase")}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ) : null}
            </Box>
          )}

          {step === 3 && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography variant="h6">Choose products</Typography>
              {statusRow(
                "Website assortment",
                productReady,
                productReady
                  ? `${productCount.toLocaleString()} product${productCount === 1 ? "" : "s"} selected`
                  : "No website products selected yet"
              )}
              <Typography variant="body2" color="text.secondary">
                Direct PromoWorld/custom products and supplier catalogues can be combined. You can launch with no products and sync them later.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="outlined" startIcon={<Inventory2RoundedIcon />} onClick={onOpenProducts}>
                  Add products
                </Button>
                <Button variant="outlined" onClick={onOpenCatalogues}>Add supplier catalogues</Button>
              </Box>
              <TextField
                select
                label="Product sync on launch"
                value={form.syncProducts ? "yes" : "no"}
                onChange={(event) => setField("syncProducts", event.target.value === "yes")}
                fullWidth
              >
                <MenuItem value="yes">Sync selected products during launch</MenuItem>
                <MenuItem value="no">Launch design now; sync products later</MenuItem>
              </TextField>
            </Box>
          )}

          {step === 4 && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography variant="h6">Store behaviour</Typography>
              <TextField
                select
                label="Launch mode"
                value={form.storeMode}
                onChange={(event) => setField("storeMode", event.target.value)}
                fullWidth
              >
                <MenuItem value="quote">Request Quote / Estimate</MenuItem>
                <MenuItem value="checkout" disabled={settings?.templateCapabilities?.environment?.stripe_available !== true}>
                  Online Checkout{settings?.templateCapabilities?.environment?.stripe_available === true ? "" : " (Stripe not detected)"}
                </MenuItem>
                <MenuItem value="catalog">Catalogue only</MenuItem>
              </TextField>
              <Typography variant="body2" color="text.secondary">
                Quote mode keeps checkout focused on estimate requests. Online checkout enables configured payment gateways. Catalogue-only publishes the storefront while disabling purchasing.
              </Typography>
            </Box>
          )}

          {step === 5 && (
            <Box sx={{ display: "grid", gap: 1.4 }}>
              <Typography variant="h6">{launchVerification ? "Launch verification" : "Ready to launch"}</Typography>
              {!launchVerification ? (
                <>
                  {statusRow("WordPress connection", connectionReady, settings?.promoShowcaseUrl || "")}
                  {statusRow("Brand", !!String(form.brandName || "").trim(), form.brandName || "Missing store name")}
                  {statusRow("Contact", !!String(form.contactEmail || form.contactPhone || "").trim(), form.contactEmail || form.contactPhone || "Optional, but recommended")}
                  {statusRow("SEO basics", !!String(form.metaDescription || form.tagline || "").trim(), form.metaDescription || form.tagline || "Optional, but recommended")}
                  {statusRow("Site design", !!selectedTemplate, selectedTemplate?.name || "No design selected")}
                  {statusRow("Products", !form.syncProducts || productReady, form.syncProducts ? `${productCount} selected` : "Sync later")}
                  <Divider />
                  <Typography variant="body2" color="text.secondary">
                    Launch publishes the site identity, design, managed pages, menus, store configuration and optional product sync, then verifies the real WordPress site.
                  </Typography>
                </>
              ) : (
                <>
                  <Chip
                    label={launchVerification.ready ? "Launch complete" : "Needs attention"}
                    color={launchVerification.ready ? "success" : "warning"}
                    sx={{ justifySelf: "start", fontWeight: 800 }}
                  />
                  {(launchVerification.checks || []).map((check) => (
                    <Box
                      key={check.key}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: .7 }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{check.label}</Typography>
                        {check.detail ? <Typography variant="caption" color="text.secondary">{check.detail}</Typography> : null}
                      </Box>
                      <Chip
                        size="small"
                        color={check.ok ? "success" : "warning"}
                        label={check.ok ? "Passed" : "Attention"}
                      />
                    </Box>
                  ))}
                  <Divider />
                  <Typography variant="caption" color="text.secondary">
                    Verified {launchVerification.checked_at_utc ? new Date(launchVerification.checked_at_utc).toLocaleString() : "just now"}.
                  </Typography>
                  {(settings?.promoShowcaseHosting?.publicUrl || settings?.promoShowcaseHosting?.effectivePublicUrl || launchVerification.site_url) ? (
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewRoundedIcon />}
                      onClick={() => window.open(
                        settings?.promoShowcaseHosting?.publicUrl
                        || settings?.promoShowcaseHosting?.effectivePublicUrl
                        || launchVerification.site_url,
                        "_blank"
                      )}
                    >
                      Open live site
                    </Button>
                  ) : null}
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={launching}>Cancel</Button>
        {step > 0 ? <Button onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={launching}>Back</Button> : null}
        {step < stepLabels.length - 1 ? (
          <Button variant="contained" onClick={() => setStep((value) => Math.min(stepLabels.length - 1, value + 1))} disabled={!canContinue || launching}>
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<RocketLaunchRoundedIcon />}
            disabled={!canContinue || launching}
            onClick={() => onLaunch(form)}
          >
            {launching ? "Launching..." : launchVerification ? "Run launch again" : "Launch Showcase"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
